import { supabase } from '@/lib/supabase'

export type CryptoAsset = {
  id: string
  symbol: string
  base_asset: string
  quote_asset: string
  name?: string | null
  logo_url?: string | null
  is_active?: boolean | null
  created_at?: string
  updated_at?: string
}

export async function listCryptoAssets(): Promise<CryptoAsset[]> {
  const { data, error } = await supabase
    .from('crypto_assets')
    .select('*')
    .order('symbol', { ascending: true })
  if (error) throw error
  return (data as any[]) as CryptoAsset[]
}

export async function listCryptoAssetsBySymbols(symbols: string[]): Promise<CryptoAsset[]> {
  const unique = Array.from(
    new Set(
      (symbols || [])
        .map((s) => String(s || '').trim().toUpperCase())
        .filter(Boolean),
    ),
  )
  if (!unique.length) return []
  const { data, error } = await supabase
    .from('crypto_assets')
    .select('*')
    .in('symbol', unique)
  if (error) throw error
  const rows = ((data as any[]) ?? []) as CryptoAsset[]
  const bySymbol = new Map<string, CryptoAsset>()
  for (const row of rows) {
    const key = String(row.symbol || '').trim().toUpperCase()
    if (!key || bySymbol.has(key)) continue
    bySymbol.set(key, row)
  }
  return unique
    .map((sym) => bySymbol.get(sym))
    .filter((it): it is CryptoAsset => !!it)
}

export async function listCryptoAssetsPaged(params: { page?: number; pageSize?: number; q?: string; onlyActive?: boolean } = {}): Promise<{ items: CryptoAsset[]; total: number }>{
  const page = Math.max(0, params.page ?? 0)
  const pageSize = Math.min(Math.max(1, params.pageSize ?? 25), 200)
  const from = page * pageSize
  const to = from + pageSize - 1
  let query = supabase
    .from('crypto_assets')
    .select('*', { count: 'exact' })
    .order('symbol', { ascending: true })
  if (params.q && params.q.trim()) {
    const q = params.q.trim()
    query = query.or(`symbol.ilike.%${q}%,name.ilike.%${q}%`)
  }
  if (params.onlyActive) {
    query = query.eq('is_active', true)
  }
  const { data, count, error } = await query.range(from, to)
  if (error) throw error
  return { items: (data as any[] ?? []) as CryptoAsset[], total: count ?? 0 }
}

export async function insertCryptoAsset(payload: {
  symbol: string
  base_asset: string
  quote_asset: string
  name?: string
  logo_url?: string | null
}): Promise<{ id: string }>{
  const { data, error } = await supabase
    .from('crypto_assets')
    .insert({
      symbol: payload.symbol,
      base_asset: payload.base_asset,
      quote_asset: payload.quote_asset,
      name: payload.name ?? null,
      logo_url: payload.logo_url ?? null,
      // Por padrão, novos ativos ficam INATIVOS até o usuário ativar e escolher a cobertura
      is_active: false,
    })
    .select('id')
    .single()
  if (error) throw error
  return { id: (data as any).id as string }
}

export async function updateCryptoLogo(id: string, logoUrl: string): Promise<void> {
  const { error } = await supabase
    .from('crypto_assets')
    .update({ logo_url: logoUrl })
    .eq('id', id)
  if (error) throw error
}

export async function updateCryptoName(id: string, name: string): Promise<void> {
  const n = (name || '').trim()
  if (!n || n.length < 2) throw new Error('Nome inválido')
  const { error } = await supabase
    .from('crypto_assets')
    .update({ name: n })
    .eq('id', id)
  if (error) throw error
}

export async function listCryptoLogos(limit: number = 40): Promise<Array<{ id: string; symbol: string; base_asset?: string; logo_url: string }>> {
  const { data, error } = await supabase
    .from('crypto_assets')
    .select('id,symbol,base_asset,logo_url')
    .eq('is_active', true)
    .not('logo_url', 'is', null)
    .order('symbol', { ascending: true })
    .limit(limit)
  if (error) throw error
  return (data as any[] || []).filter(r => r.logo_url)
}

export type SafeDeleteDryRun = {
  ok: boolean
  dry_run: true
  counts: Record<string, number | string>
}

export type SafeDeleteResult = {
  ok: boolean
  dry_run: false
  hard: boolean
  counts: Record<string, number | string>
}

export async function safeDeleteAssetDryRun(symbol: string): Promise<SafeDeleteDryRun> {
  const { data, error } = await supabase.rpc('safe_delete_asset', { p_symbol: symbol, p_dry_run: true, p_hard: false })
  if (error) throw error
  return data as SafeDeleteDryRun
}

export async function safeDeleteAsset(symbol: string, params: { hard?: boolean } = {}): Promise<SafeDeleteResult> {
  const { data, error } = await supabase.rpc('safe_delete_asset', { p_symbol: symbol, p_dry_run: false, p_hard: !!params.hard })
  if (error) throw error
  return data as SafeDeleteResult
}

export type TimeframeHealth = {
  symbol: string
  warnings: number
  timeframes: Array<{ timeframe: string; count: number; latest: string | null; has500: boolean; stale_seconds: number | null; gap_count: number | null; status: 'ok' | 'warning' }>
}

export async function timeframeHealthSymbol(symbol: string): Promise<TimeframeHealth> {
  const { data, error } = await supabase.rpc('timeframe_health_symbol', { p_symbol: symbol })
  if (error) throw error
  return data as TimeframeHealth
}

export type TimeframeHealthOverview = {
  results: TimeframeHealth[]
  generated_at: string
}

export async function timeframeHealthOverview(): Promise<TimeframeHealthOverview> {
  const { data, error } = await supabase.rpc('timeframe_health_overview')
  if (error) throw error
  return data as TimeframeHealthOverview
}

export type CurrentBucket = {
  open_time: string | null
  expected_close_time: string | null
  progress_pct: number | null
  open: number | null
  high: number | null
  low: number | null
  close: number | null
  volume: number | null
  slices: number | null
}

export async function currentBucketFrom5m(symbol: string, timeframe: string): Promise<CurrentBucket | null> {
  const { data, error } = await supabase.rpc('current_bucket_from_5m', { p_symbol: symbol, p_tf: timeframe })
  if (error) throw error
  return (data as any) as CurrentBucket
}

export async function activateAssetSeed(symbol: string): Promise<any> {
  // Fast-path: Edge Function nao-bloqueante (evita timeout do PostgREST)
  try {
    const fast = await supabase.functions.invoke('activate-asset-seed-lite', { body: { symbol } })
    if (fast.error) throw fast.error
    return fast.data
  } catch (_) {
    // Fallback: RPC legada (mantem compatibilidade)
    const { data, error } = await supabase.rpc('activate_asset_seed', { p_symbol: symbol, p_burst: true })
    if (error) throw error
    return data
  }
}

export async function deactivateAssetCleanup(symbol: string): Promise<any> {
  const { data, error } = await supabase.rpc('deactivate_asset_cleanup', { p_symbol: symbol })
  if (error) throw error
  return data
}
export async function fastBootstrapSymbol(symbol: string, backBuckets: number = 300): Promise<any> {
  const { data, error } = await supabase.rpc('fast_bootstrap_symbol', { p_symbol: symbol, p_back_buckets: backBuckets })
  if (error) throw error
  return data
}

